import {
  REACT_APP_NEW_RELIC_URL,
  REACT_APP_RELEASE_COMMIT,
  REACT_APP_STAGE,
} from "@env";
import { version } from "../../../../../package.json";

const configuration = {
  url: REACT_APP_NEW_RELIC_URL,
  environment: REACT_APP_STAGE,
  commit: REACT_APP_RELEASE_COMMIT,
};

type BrowserTelemetryAgent = {
  noticeError: (error: Error) => void;
  setCustomAttribute?: (name: string, value: string) => void;
};

const runtime: { mutable_initialization?: Promise<void> } = {};

const loadBrowserAgent = (url: string): Promise<void> =>
  new Promise((resolve) => {
    const script = document.createElement("script");
    script.setAttribute("src", url);
    script.setAttribute("async", "");
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => resolve(), { once: true });
    document.head.appendChild(script);
  });

const waitForTelemetry = (startup: Promise<void>): Promise<void> =>
  new Promise((resolve) => {
    const timeout = window.setTimeout(resolve, 3000);
    const finish = (): void => {
      window.clearTimeout(timeout);
      resolve();
    };
    void startup.then(finish, finish);
  });

const initializeBrowserTelemetry = (config = configuration): Promise<void> => {
  if (typeof window === "undefined" || !config.url) {
    return Promise.resolve();
  }
  if (runtime.mutable_initialization) return runtime.mutable_initialization;

  runtime.mutable_initialization = waitForTelemetry(
    loadBrowserAgent(config.url).then(() => {
      const agent = (window as Window & { newrelic?: BrowserTelemetryAgent })
        .newrelic;
      agent?.setCustomAttribute?.(
        "frontend.application",
        "carrier-approvals-legacy"
      );
      agent?.setCustomAttribute?.("frontend.environment", config.environment);
      agent?.setCustomAttribute?.("frontend.version", version);
      if (config.commit)
        agent?.setCustomAttribute?.("frontend.commit", config.commit);
    })
  );

  return runtime.mutable_initialization;
};

const reportBrowserError = (error: Error): void => {
  try {
    (
      window as Window & { newrelic?: BrowserTelemetryAgent }
    ).newrelic?.noticeError(error);
  } catch {
    // Keep the error fallback usable if the agent itself fails.
  }
};

/*
 * Future npm migration reference, intentionally inactive.
 * Keep the URL loader above as the only active implementation in Expo 40.
 * Before using this code, restore @newrelic/browser-agent (1.324.0 in Nx),
 * validate the bundler/transforms, and supply accountId/applicationId/licenseKey.
 * Replace the URL loader rather than running both agents.
 *
 * const collector = "bam.nr-data.net";
 * const { BrowserAgent } = await import(
 *   "@newrelic/browser-agent/dist/esm/loaders/browser-agent"
 * );
 * const agent = new BrowserAgent({
 *   info: {
 *     applicationID: config.applicationId,
 *     beacon: collector,
 *     errorBeacon: collector,
 *     licenseKey: config.licenseKey,
 *     sa: 1,
 *   },
 *   init: {
 *     ajax: { deny_list: [collector] },
 *     browser_consent_mode: { enabled: false },
 *     distributed_tracing: { enabled: true },
 *     performance: {
 *       capture_detail: false,
 *       capture_marks: false,
 *       capture_measures: true,
 *     },
 *     privacy: { cookies_enabled: true },
 *     session_replay: { enabled: false },
 *     logging: { enabled: false },
 *     user_actions: { enabled: false },
 *   },
 *   loader_config: {
 *     accountID: config.accountId,
 *     agentID: config.applicationId,
 *     applicationID: config.applicationId,
 *     licenseKey: config.licenseKey,
 *     trustKey: config.accountId,
 *   },
 * });
 * agent.setCustomAttribute(
 *   "frontend.application",
 *   "carrier-approvals-legacy"
 * );
 * agent.setCustomAttribute("frontend.environment", config.environment);
 * agent.setCustomAttribute("frontend.version", version);
 * if (config.commit)
 *   agent.setCustomAttribute("frontend.commit", config.commit);
 */

export { initializeBrowserTelemetry, reportBrowserError };
